import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ChallengeError } from '@backbase/identity-core-ang';
import { CountdownService } from '../../../shared/countdown/countdown.service';
import { Observable } from 'rxjs';
import { ChallengeSmsOtpData, SmsOtpResponse } from './sms-otp.model';

@Component({
  selector: 'bb-transaction-signing-sms-otp',
  templateUrl: 'sms-otp.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionSigningSmsOtpComponent implements OnChanges {
  @Output() cancel = new EventEmitter<void>();
  /* tslint:disable-next-line */
  @Output() complete = new EventEmitter<SmsOtpResponse>();
  @Output() acceptError = new EventEmitter<Error>();
  @Output() resend = new EventEmitter<void>();
  @Input() previousData: ChallengeSmsOtpData | undefined;
  sent = false;

  @Input()
  set data(value: ChallengeSmsOtpData) {
    this.setOtpValidators(value.expectedOtpLength);
    this.clearAlerts();
    this.setResendCountdown(value);
  }

  get data() {
    if (!this.challengeData) {
      throw new Error('No challenge data set');
    }
    return this.challengeData;
  }

  @Input()
  set error(value: ChallengeError | undefined) {
    if (!value || !this.isRecognizedError(value)) {
      this.errorValue = value;
      return;
    }

    this.clearAlerts();
    this.form.reset();
    this.form.markAllAsTouched();
    this.displayMissingAlert = true;
  }

  get error() {
    return this.errorValue;
  }

  private readonly invalidRequestError = 'invalid_request';
  readonly form: FormGroup = this.formBuilder.group({
    otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });
  readonly timeUntilResend$: Observable<number> = this.countdown.remainingTime$;
  errorValue: ChallengeError | undefined;
  challengeData: ChallengeSmsOtpData | undefined;
  displayIncorrectAlert = false;
  displayMissingAlert = false;
  displayRemainingAlert = false;
  displayResendAlert = false;

  constructor(private readonly formBuilder: FormBuilder, private countdown: CountdownService) {}

  ngOnChanges(changes: any) {
    this.setResendOptions(changes.data.currentValue.remainingOtpRequests);
    this.setRemainingOptions(changes.data.currentValue.remainingAuthenticationAttempts);
    this.challengeData = { ...this.previousData, ...changes.data.currentValue };
  }

  onComplete() {
    this.clearAlerts();
    if (this.form.valid) {
      return this.complete.emit({ ...this.form.value } as SmsOtpResponse);
    }

    if (!this.form.value.otp || !this.form.value.otp.length) {
      this.displayMissingAlert = true;
      this.form.markAllAsTouched();
    }

    if (this.form.controls.otp.errors && this.form.controls.otp.errors.pattern) {
      this.displayIncorrectAlert = true;
      this.form.reset();
      this.form.markAllAsTouched();
    }
  }

  showInvalidError() {
    return (this.displayIncorrectAlert || this.displayRemainingAlert) && !this.form.value.otp;
  }

  showMissingError() {
    return this.displayMissingAlert && !this.form.value.otp;
  }

  private clearAlerts() {
    this.displayIncorrectAlert = false;
    this.displayMissingAlert = false;
    this.displayRemainingAlert = false;
    this.displayResendAlert = false;
  }

  private setOtpValidators(patternLength: number) {
    const regex = new RegExp('^\\d{' + patternLength + '}$');
    this.form.controls.otp.setValidators([Validators.required, Validators.pattern(regex)]);
  }

  private isRecognizedError(error: ChallengeError) {
    return error.errorCode === this.invalidRequestError && error.errorDescription === this.invalidRequestError;
  }

  private setResendCountdown(challenge: ChallengeSmsOtpData) {
    if (challenge.nextOtpTime > 0 && challenge.remainingOtpRequests !== 0) {
      this.countdown.start(challenge.nextOtpTime);
    }
  }

  private setResendOptions(remaining?: number) {
    if (remaining === undefined || !this.previousData) {
      return;
    }

    const previousRemaining = this.previousData && this.previousData.remainingOtpRequests;
    if (!previousRemaining || remaining < previousRemaining) {
      this.displayResendAlert = true;
      this.displayRemainingAlert = false;
    }
  }

  private setRemainingOptions(remaining?: number) {
    if (remaining === undefined || !this.previousData) {
      return;
    }

    const previousRemaining = this.previousData.remainingAuthenticationAttempts;
    if (!previousRemaining || remaining < previousRemaining) {
      this.form.reset();
      this.form.markAllAsTouched();
      this.displayRemainingAlert = true;
      this.displayResendAlert = false;
    }
  }
}
